import React, { useCallback, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@frontend/shadcn/components/ui/collapsible';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { Label } from '@frontend/shadcn/components/ui/label';
import { Select } from '@revfluence/fresh';
import { ChevronUpIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { RadioGroup, RadioGroupItem } from '@frontend/shadcn/components/ui/radio-group';
import { filtersFormSchema } from '../../hooks/use-filters-form';
import { ageOptions, genderOptions, interestOptions, followerOptions, engagementOptions, countriesOptions } from './options';

export type FiltersFormData = z.infer<typeof filtersFormSchema>;

export type FiltersFormProps = {
  onSubmit: (data: FiltersFormData) => void;
  onCancel: () => void;
  form: UseFormReturn<FiltersFormData>;
};

export const FiltersForm: React.FC<FiltersFormProps> = ({
  form,
  onSubmit,
  onCancel,
}) => {
  const handleSubmit = (data: z.infer<typeof filtersFormSchema>) => {
    const formattedData = {
      ...data,
      genders: data.genders || [],
      query: data.query || '',
      ageBuckets: data.ageBuckets || [],
      countries: data.countries || [],
      minFollowers: data.minFollowers,
      maxFollowers: data.maxFollowers,
      minEngagements: data.minEngagements,
      maxEngagements: data.maxEngagements,
      interests: data.interests || [],
      majorAudienceAgeBuckets: data.majorAudienceAgeBuckets || [],
      majorAudienceGenders: data.majorAudienceGenders || ['ALL'],
      majorAudienceCountries: data.majorAudienceCountries || []
    };
    onSubmit(formattedData);
  };

  const filterOption = useCallback((search, option) => option.label.toLowerCase().includes(search.toLowerCase()), []);

  const { formState: { errors } } = form;

  const [isCreatorOpen, setIsCreatorOpen] = useState(true);
  const [isAudienceOpen, setIsAudienceOpen] = useState(false);

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <Collapsible
          open={isCreatorOpen}
          onOpenChange={setIsCreatorOpen}
          defaultOpen
          className="border-b"
        >
          <CollapsibleTrigger
            className="flex w-full items-center justify-between py-6"
            data-dd-action-name="toggle-creator-section"
          >
            <span className="font-medium">Creator Demographics</span>
            {
              isCreatorOpen ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )
            }
          </CollapsibleTrigger>
          <CollapsibleContent className="pb-6 space-y-6">
            <div className="space-y-3">
              <Label>Age</Label>
              <div className="grid grid-cols-2 gap-2">
                <Controller
                  name="ageBuckets"
                  control={form.control}
                  render={({ field }) => (
                    <>
                      {ageOptions.map((option) => (
                        <div key={option.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={`age-${option.value}`}
                            checked={Array.isArray(field.value) && field.value.includes(option.value)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                field.onChange([...(Array.isArray(field.value) ? field.value : []), option.value]);
                              } else {
                                field.onChange(Array.isArray(field.value)
                                ? field.value.filter((age) => age !== option.value)
                                : []);
                              }
                            }}
                            data-dd-action-name={`toggle-${option.value}`}
                            aria-label={`Age ${option.label}`}
                          />
                          <label htmlFor={`age-${option.value}`} className="text-sm font-medium">
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </>
                  )}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Gender</Label>
              <Controller
                name="genders"
                control={form.control}
                render={({ field }) => (
                  <RadioGroup
                    onValueChange={(value) => {
                      field.onChange([value]);
                    }}
                    defaultValue={field.value?.[0] || 'ALL'}
                    value={field.value?.[0] || 'ALL'}
                    className="flex flex-row space-x-4"
                  >
                    {genderOptions.map((option) => (
                      <div key={option.value} className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={option.value}
                          id={`gender-${option.value}`}
                          data-dd-action-name={`select-gender-${option.value}`}
                        />
                        <label htmlFor={`gender-${option.value}`} className="text-sm font-medium cursor-pointer">
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              />
            </div>

            <div className="space-y-3">
              <Label>Locations</Label>
              <Controller
                name="countries"
                control={form.control}
                render={({ field }) => (
                  <Select
                    mode="multiple"
                    options={countriesOptions}
                    value={field.value || []}
                    onChange={(values) => {
                      field.onChange(values);
                    }}
                    placeholder="Enter a location"
                    filterOption={filterOption}
                    showSearch
                    showArrow
                    popupClassName="countries-popup"
                    style={{ width: '100%' }}
                    getPopupContainer={(trigger) => trigger.parentNode}
                    data-dd-action-name="select-countries"
                    aria-label="Select countries"
                  />
                )}
              />
            </div>

            <div className="space-y-3">
              <Label>Followers</Label>
              <div className="flex items-start space-x-3">
                <Controller
                  name="minFollowers"
                  control={form.control}
                  render={({ field }) => (
                    <div className="flex flex-col w-[45%]">
                      <Select
                        options={followerOptions}
                        value={field.value || null}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                        placeholder="Min"
                        style={{ width: '100%' }}
                        allowClear
                        getPopupContainer={(trigger) => trigger.parentNode}
                        data-dd-action-name="select-min-followers"
                        aria-label="Minimum followers"
                        status={errors.minFollowers ? 'error' : undefined}
                      />
                      {errors.minFollowers && (
                        <span className="text-sm text-red-500 mt-1">{errors.minFollowers.message}</span>
                      )}
                    </div>
                  )}
                />
                <span className="text-sm mt-2">to</span>
                <Controller
                  name="maxFollowers"
                  control={form.control}
                  render={({ field }) => (
                    <div className="flex flex-col w-[45%]">
                      <Select
                        options={followerOptions}
                        value={field.value || null}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                        placeholder="Max"
                        style={{ width: '100%' }}
                        allowClear
                        getPopupContainer={(trigger) => trigger.parentNode}
                        data-dd-action-name="select-max-followers"
                        aria-label="Maximum followers"
                        status={errors.maxFollowers ? 'error' : undefined}
                      />
                      {errors.maxFollowers && (
                        <span className="text-sm text-red-500 mt-1">{errors.maxFollowers.message}</span>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Engagements</Label>
              <div className="flex items-start space-x-3">
                <Controller
                  name="minEngagements"
                  control={form.control}
                  render={({ field }) => (
                    <div className="flex flex-col w-[45%]">
                      <Select
                        options={engagementOptions}
                        value={field.value || null}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                        placeholder="Min"
                        style={{ width: '100%' }}
                        allowClear
                        getPopupContainer={(trigger) => trigger.parentNode}
                        data-dd-action-name="select-min-engagements"
                        aria-label="Minimum engagements"
                        status={errors.minEngagements ? 'error' : undefined}
                      />
                      {errors.minEngagements && (
                        <span className="text-sm text-red-500 mt-1">{errors.minEngagements.message}</span>
                      )}
                    </div>
                  )}
                />
                <span className="text-sm">to</span>
                <Controller
                  name="maxEngagements"
                  control={form.control}
                  render={({ field }) => (
                    <div className="flex flex-col w-[45%]">
                      <Select
                        options={engagementOptions}
                        value={field.value || null}
                        onChange={(value) => {
                          field.onChange(value);
                        }}
                        placeholder="Max"
                        style={{ width: '100%' }}
                        allowClear
                        getPopupContainer={(trigger) => trigger.parentNode}
                        data-dd-action-name="select-max-engagements"
                        aria-label="Maximum engagements"
                        status={errors.maxEngagements ? 'error' : undefined}
                      />
                      {errors.maxEngagements && (
                        <span className="text-sm text-red-500 mt-1">{errors.maxEngagements.message}</span>
                      )}
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Interests</Label>
              <Controller
                name="interests"
                control={form.control}
                render={({ field }) => (
                  <Select
                    mode="multiple"
                    options={interestOptions}
                    value={field.value || []}
                    onChange={(values) => {
                      field.onChange(values);
                    }}
                    placeholder="Select interests"
                    filterOption={filterOption}
                    showSearch
                    showArrow
                    popupClassName="interests-popup"
                    style={{ width: '100%' }}
                    getPopupContainer={(trigger) => trigger.parentNode}
                    data-dd-action-name="select-interests"
                    aria-label="Select interests"
                  />
                )}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>

        <Collapsible
          open={isAudienceOpen}
          onOpenChange={setIsAudienceOpen}
          className="border-b"
        >
          <CollapsibleTrigger
            className="flex w-full items-center justify-between py-6"
            data-dd-action-name="toggle-audience-section"
          >
            <span className="font-medium">Audience Demographics</span>
            {
              isAudienceOpen ? (
                <ChevronUpIcon className="h-4 w-4" />
              ) : (
                <ChevronDownIcon className="h-4 w-4" />
              )
            }
          </CollapsibleTrigger>
          <CollapsibleContent className="pb-6 space-y-6">
            <div className="space-y-3">
              <Label>Age</Label>
              <div className="grid grid-cols-2 gap-2">
                <Controller
                  name="majorAudienceAgeBuckets"
                  control={form.control}
                  render={({ field }) => (
                    <>
                      {ageOptions.map((option) => (
                        <div key={option.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={`audience-age-${option.value}`}
                            checked={Array.isArray(field.value) && field.value.includes(option.value)}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                field.onChange([...(Array.isArray(field.value) ? field.value : []), option.value]);
                              } else {
                                field.onChange(Array.isArray(field.value)
                                  ? field.value.filter((age) => age !== option.value)
                                  : []);
                              }
                            }}
                            data-dd-action-name={`toggle-audience-${option.value}`}
                            aria-label={`Audience Age ${option.label}`}
                          />
                          <label htmlFor={`audience-age-${option.value}`} className="text-sm font-medium">
                            {option.label}
                          </label>
                        </div>
                      ))}
                    </>
                  )}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Gender</Label>
              <Controller
                name="majorAudienceGenders"
                control={form.control}
                render={({ field }) => (
                  <RadioGroup
                    onValueChange={(value) => field.onChange([value])}
                    defaultValue={field.value?.[0] || 'ALL'}
                    value={field.value?.[0] || 'ALL'}
                    className="flex flex-row space-x-4"
                  >
                    {genderOptions.map((option) => (
                      <div key={option.value} className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={option.value}
                          id={`audience-gender-${option.value}`}
                          data-dd-action-name={`select-audience-gender-${option.value}`}
                        />
                        <label htmlFor={`audience-gender-${option.value}`} className="text-sm font-medium cursor-pointer">
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              />
            </div>

            <div className="space-y-3">
              <Label>Locations</Label>
              <Controller
                name="majorAudienceCountries"
                control={form.control}
                render={({ field }) => (
                  <Select
                    mode="multiple"
                    options={countriesOptions}
                    value={field.value || []}
                    onChange={(values) => {
                      field.onChange(values);
                    }}
                    placeholder="Enter a location"
                    filterOption={filterOption}
                    showSearch
                    showArrow
                    popupClassName="audience-countries-popup"
                    style={{ width: '100%' }}
                    getPopupContainer={(trigger) => trigger.parentNode}
                    data-dd-action-name="select-audience-countries"
                    aria-label="Select audience countries"
                  />
                )}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>

      <div className="flex justify-start gap-2 p-4 mt-auto mb-4 border-t bg-background mx-[-1.5rem]">
        <Button
          type="submit"
          variant="primary"
          size="sm"
          data-dd-action-name="apply-filters"
          aria-label="Apply filters"
          disabled={form.formState.isSubmitting || Object.keys(form.formState.errors).length > 0}
        >
          {form.formState.isSubmitting ? 'Applying...' : 'Apply'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          data-dd-action-name="cancel-filters"
          aria-label="Cancel filters"
          disabled={form.formState.isSubmitting}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};
